import React, { useState } from "react";
import { useClaims } from "../../auth/useClaims";
import DashboardSectionCard from "../components/DashboardSectionCard";
import ShiftChips from "../../guarding/components/attendance/ShiftChips";
import AttendanceDonut, { AttendanceStatRow, ScopeChip } from "../../guarding/components/attendance/AttendanceDonut";
import { useGetAttendanceSummaryQuery } from "../../guarding/guardingApi";
import { paths } from "../../../routes/paths";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";

/**
 * Today's Attendance on the main dashboard - the same donut and the same
 * Now/Day/Night filter as the full Attendance Dashboard page, in one box.
 *
 * Deliberately the headline figure only. Marked / Not Marked / Mismatches are
 * lists you work through, which is what the full page under Guarding is for -
 * the "View All" link goes there, carrying nothing, because the page resolves
 * "Now" for itself the same way this does.
 *
 * The shift selection is local to this box: switching it here re-drives only this
 * card, not the rest of the dashboard, which is filtered by the header's period
 * and branch instead. The period does not apply here (always one shift on one
 * day), but since 2026-10-02 the header BRANCH does - the summary endpoint takes
 * an optional branchId. The My Areas / All Areas chip still states the area scope.
 *
 * Refreshed every 90 seconds, like the full page.
 */
const REFRESH_MS = 90000;

const AttendanceTodayCard = () => {
  // Matches the endpoint's own "Attendance.Mark|Attendance.View" policy, and the
  // Guarding menu's own gate for this screen. Both are read unconditionally -
  // `a() || b()` would short-circuit the second hook and change the hook count
  // between renders.
  const { permissions } = useClaims();
  const canView = permissions.includes("Attendance.Mark") || permissions.includes("Attendance.View");

  const [shift, setShift] = useState(null);
  // Follows the header branch (2026-10-02); null = all the caller's branches.
  const { selectedBranchId } = useBranchFilter();
  const params = { ...(shift ? { shift } : {}), ...(selectedBranchId != null ? { branchId: selectedBranchId } : {}) };
  const { data: summary, isLoading } = useGetAttendanceSummaryQuery(params, {
    skip: !canView,
    pollingInterval: REFRESH_MS,
  });

  if (!canView) return null;

  return (
    <div className="col-12 col-xl-4">
      <DashboardSectionCard
        title="Today's Attendance"
        viewAllPath={paths.guardingAttendanceDashboard}
        isLoading={isLoading}
      >
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
          <ShiftChips value={shift} onChange={setShift} nowShift={summary?.shift} size="sm" />
          <ScopeChip isScopedToMyAreas={Boolean(summary?.isScopedToMyAreas)} />
        </div>

        <AttendanceDonut
          marked={summary?.markedCount || 0}
          notMarked={summary?.notMarkedCount || 0}
          total={summary?.totalAssignments || 0}
          percent={summary?.percentComplete || 0}
          height={150}
        />

        <div className="mt-2">
          <AttendanceStatRow
            total={summary?.totalAssignments || 0}
            marked={summary?.markedCount || 0}
            notMarked={summary?.notMarkedCount || 0}
            compact
          />
        </div>

        {/* Zero assignments is not zero progress - it is "nothing to mark", which
            the donut alone would show as a grey 0% and read as a bad day. */}
        {summary?.totalAssignments === 0 && (
          <p className="text-muted small text-center mb-0 mt-2">No duty slots assigned for this shift.</p>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default AttendanceTodayCard;
